"use client";

import { useRef, useState } from "react";
import Image, { StaticImageData } from "next/image";
import { useTranslations } from "next-intl";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import type { Swiper as SwiperType } from "swiper";

import { cn } from "@/lib/utils";

import cncImg from "@/../public/image/new-version/home/factory/cnc-machining-factory.jpg";
import sheetMetalImg from "@/../public/image/new-version/home/factory/sheet-metal-fabrication-workshop.jpg";
import injectionImg from "@/../public/image/new-version/home/factory/injection-molding-shop.jpg";
import printingImg from "@/../public/image/new-version/home/factory/3d-printing-factory.jpg";
import vacuumImg from "@/../public/image/new-version/home/factory/vacuum-casting-shop.webp";
import dieImg from "@/../public/image/new-version/home/factory/die-casting-factory.jpg";

import "swiper/css";
import "swiper/css/navigation";

// ─── slug → 翻译命名空间（与 ServiceBanner 的 SLUG_CONFIG 一致扩展）───

interface SlugConfig {
	namespace: string;
}

const SLUG_CONFIG: Record<string, SlugConfig> = {
	bending: { namespace: "metalBendingFactoryShowcase" },
	"cnc-machining": { namespace: "cncMillingFactoryShowcase" },
	"cnc-turning": { namespace: "cncMillingFactoryShowcase" },
	"gear-machining": { namespace: "cncMillingFactoryShowcase" },
	"5-axis-cnc-machining": { namespace: "fiveAxisFactoryShowcase" },
	"injection-molding": { namespace: "injectionMoldingFactoryShowcase" },
	"sheet-metal-fabrication": { namespace: "sheetMetalFabricationFactoryShowcase" },
	"metal-casting": { namespace: "metalCastingFactoryShowcase" },
};

const FALLBACK_FACTORY_NS = "factoryShowcase";

// ─── 子组件 ───

function ExpandIcon({ size = 16, className = "" }: { size?: number; className?: string }) {
	return (
		<svg
			width={size}
			height={size}
			viewBox="0 0 16 16"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
			className={className}
		>
			<path
				d="M15.5938 1.125L10.6875 6H14H14.5V7H14H9.5H9V6.5V2V1.5H10V2V5.3125L14.875 0.40625L15.25 0.0625L15.9375 0.75L15.5938 1.125ZM0.375 14.9062L5.28125 10H2H1.5V9H2H6.5H7V9.5V14V14.5H6V14V10.7188L1.09375 15.625L0.75 15.9688L0.03125 15.25L0.375 14.9062Z"
				fill="currentColor"
			/>
		</svg>
	);
}

export interface FactoryData {
	id: number;
	titleKey: string;
	establishedKey: string;
	machinesKey: string;
	locationKey: string;
	image: StaticImageData | string;
}

interface MultiServiceFactoryShowcaseProps {
	/** 自定义页 slug，与 `customLayouts.ts` 中 key 一致；首页不传则使用 `factoryShowcase` */
	slug?: string;
	className?: string;
	factories?: FactoryData[];
}

const factoriesData: FactoryData[] = [
	{
		id: 1,
		titleKey: "cncTitle",
		establishedKey: "cncEstablished",
		machinesKey: "cncMachines",
		locationKey: "cncLocation",
		image: cncImg,
	},
	{
		id: 2,
		titleKey: "sheetMetalTitle",
		establishedKey: "sheetMetalEstablished",
		machinesKey: "sheetMetalMachines",
		locationKey: "sheetMetalLocation",
		image: sheetMetalImg,
	},
	{
		id: 3,
		titleKey: "injectionTitle",
		establishedKey: "injectionEstablished",
		machinesKey: "injectionMachines",
		locationKey: "injectionLocation",
		image: injectionImg,
	},
	{
		id: 4,
		titleKey: "printingTitle",
		establishedKey: "printingEstablished",
		machinesKey: "printingMachines",
		locationKey: "printingLocation",
		image: printingImg,
	},
	{
		id: 5,
		titleKey: "vacuumTitle",
		establishedKey: "vacuumEstablished",
		machinesKey: "vacuumMachines",
		locationKey: "vacuumLocation",
		image: vacuumImg,
	},
	{
		id: 6,
		titleKey: "dieTitle",
		establishedKey: "dieEstablished",
		machinesKey: "dieMachines",
		locationKey: "dieLocation",
		image: dieImg,
	},
];

interface FactoryCardProps {
	factory: FactoryData;
	tr: (key: string) => string;
}

function FactoryCard({ factory, tr }: FactoryCardProps) {
	const [isHovered, setIsHovered] = useState(false);

	return (
		<div
			className="group relative h-[400px] w-full overflow-hidden sm:h-[450px] sm:max-w-[370px]"
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
		>
			<Image
				src={factory.image}
				alt={tr(factory.titleKey)}
				fill
				className="object-cover transition-transform duration-500 group-hover:scale-105"
				sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
			/>

			<div
				className={`absolute bottom-0 left-0 right-0 mx-auto w-[95%] bg-[#2a2a2a] px-4 py-3 transition-all duration-500 sm:px-5 sm:py-4 ${
					isHovered ? "translate-y-full opacity-0" : "translate-y-0 opacity-100"
				}`}
			>
				<div className="flex items-center justify-between">
					<h3 className="text-white sm:text-base">{tr(factory.titleKey)}</h3>
					<ExpandIcon size={18} className="text-white" />
				</div>
			</div>

			<div
				className={`absolute bottom-0 left-0 right-0 mx-auto w-[95%] bg-[#1a1a1a]/95 p-5 transition-all duration-500 sm:p-6 ${
					isHovered ? "translate-y-0 opacity-100" : "translate-y-full opacity-0"
				}`}
			>
				<div className="mb-4 flex items-center justify-between border-b border-[#666667] pb-3 sm:mb-5 sm:pb-4">
					<h3 className="text-base text-white sm:text-base">{tr(factory.titleKey)}</h3>
					<ExpandIcon size={20} className="text-white" />
				</div>

				<div className="space-y-3 sm:space-y-4">
					<div className="flex items-center justify-between border-b border-[#666667] pb-2 sm:pb-3">
						<span className="text-gray-400 sm:text-sm">{tr("established")}</span>
						<span className="text-sm text-white">{tr(factory.establishedKey)}</span>
					</div>

					<div className="flex items-center justify-between border-b border-[#666667] pb-2 sm:pb-3">
						<span className="text-gray-400 sm:text-sm">{tr("productionCapacity")}</span>
						<span className="text-sm text-white">{tr(factory.machinesKey)}</span>
					</div>

					<div className="flex items-center justify-between">
						<span className="text-gray-400 sm:text-sm">{tr("location")}</span>
						<span className="text-sm text-white">{tr(factory.locationKey)}</span>
					</div>
				</div>
			</div>
		</div>
	);
}

export default function MultiServiceFactoryShowcase({
	slug,
	className,
	factories,
}: MultiServiceFactoryShowcaseProps) {
	const config = slug ? SLUG_CONFIG[slug] : undefined;
	const ns = config?.namespace;

	const t = useTranslations();
	const tr = (key: string) => (ns ? t(`${ns}.${key}`) : t(`${FALLBACK_FACTORY_NS}.${key}`));

	const tNav = useTranslations(FALLBACK_FACTORY_NS);

	const swiperRef = useRef<SwiperType | null>(null);
	const [isBeginning, setIsBeginning] = useState(true);
	const [isEnd, setIsEnd] = useState(false);

	const handleSlideChange = (swiper: SwiperType) => {
		setIsBeginning(swiper.isBeginning);
		setIsEnd(swiper.isEnd);
	};

	const displayFactories = factories ?? factoriesData;
	const showNavigation = displayFactories.length > 4;

	return (
		<section className={cn("bg-white py-8 sm:py-10 md:py-12 lg:py-16", className)}>
			<div className="container">
				<div className="relative">
					<Swiper
						modules={[Navigation]}
						spaceBetween={16}
						slidesPerView={1}
						breakpoints={{
							640: {
								slidesPerView: 2,
								spaceBetween: 20,
							},
							1024: {
								slidesPerView: 3,
								spaceBetween: 24,
							},
							1280: {
								slidesPerView: 4,
								spaceBetween: 24,
							},
						}}
						onSwiper={(swiper) => {
							swiperRef.current = swiper;
						}}
						onSlideChange={handleSlideChange}
						className="overflow-hidden"
					>
						{displayFactories.map((factory) => (
							<SwiperSlide key={factory.id} className="flex justify-center">
								<FactoryCard factory={factory} tr={tr} />
							</SwiperSlide>
						))}
					</Swiper>

					{showNavigation && (
						<div className="mt-6 flex items-center justify-end gap-6 sm:mt-8 2xl:mr-32">
							<button
								type="button"
								onClick={() => swiperRef.current?.slidePrev()}
								disabled={isBeginning}
								className={`transition-colors duration-200 ${
									isBeginning ? "cursor-not-allowed text-gray-300" : "text-themePrimary400 hover:text-mainColor"
								}`}
								aria-label={tNav("prevSlide")}
							>
								<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
									<path
										d="M19 12H5M5 12L12 19M5 12L12 5"
										stroke="currentColor"
										strokeWidth="2"
										strokeLinecap="round"
										strokeLinejoin="round"
									/>
								</svg>
							</button>

							<button
								type="button"
								onClick={() => swiperRef.current?.slideNext()}
								disabled={isEnd}
								className={`transition-colors duration-200 ${
									isEnd ? "cursor-not-allowed text-gray-300" : "text-themePrimary400 hover:text-mainColor"
								}`}
								aria-label={tNav("nextSlide")}
							>
								<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
									<path
										d="M5 12H19M19 12L12 5M19 12L12 19"
										stroke="currentColor"
										strokeWidth="2"
										strokeLinecap="round"
										strokeLinejoin="round"
									/>
								</svg>
							</button>
						</div>
					)}
				</div>
			</div>
		</section>
	);
}
